import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';
import * as ExcelJS from 'exceljs';
import * as FileSaver from 'file-saver';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { NwcTerminologyService } from 'app/core/transloco/nwc-terminology.service';

type IncrementalExportFormat = 'xlsx' | 'csv';

interface IncrementalWorksheetContext {
  workbook: ExcelJS.Workbook;
  worksheet: ExcelJS.Worksheet;
  headers: string[];
  imageColumnIndex: number;
  format: IncrementalExportFormat;
}

@Injectable({
  providedIn: 'root',
})
export class XlsxService {
  private readonly maxImageErrorLogs = 5;
  private imageErrorLogCount = 0;

  constructor(
    private toastrService: ToastrService,
    private translateService: TranslocoService,
    private nwcTerminologyService: NwcTerminologyService,
  ) {}

  static toExportFileName(excelFileName: string, format: string): string {
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, '0');
    const month = String(currentDate.getMonth() + 1).padStart(2, '0');
    const year = currentDate.getFullYear();
    const formattedDate = `${day}-${month}-${year}`;
    return `${excelFileName}_export_${formattedDate}.${format}`;
  }

  public exportAsExcelFile(json: any[], excelFileName: string, format: string): void {
    const header = Object.keys(json[0]);
    const map1: any = {};
    header.map((e: string) => {
      map1[e] = this.formatHeaderForExport(e);
    });
    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet([map1, ...json], {
      skipHeader: true,
    });
    const workbook: XLSX.WorkBook = {
      Sheets: { data: worksheet },
      SheetNames: ['data'],
    };
    XLSX.writeFile(
      workbook,
      XlsxService.toExportFileName(this.formatExportName(excelFileName), format),
    );
  }

  public exportMultiWorkSheet(jsons: any, excelFileName: string, format: string): void {
    const workBook: XLSX.WorkBook = { Sheets: {}, SheetNames: [] };

    Object.keys(jsons).forEach((key: any) => {
      const json = jsons[key];

      if (!json || json.length === 0) return;

      const headers = Object.keys(json[0]);
      const headerRow = headers.map((h) => this.formatHeaderForExport(h));

      // Convert JSON to array format (Array of Arrays)
      const aoaData: any[][] = [headerRow]; // First row as headers

      json.forEach((row: any) => {
        const newRow: any[] = headers.map((field) => {
          if (
            ['description', 'recommended_corrective_action', 'الوصف'].includes(field) &&
            row[field]
          ) {
            return this.convertHtmlToPlainText(row[field]); // Convert rich text to formatted plain text
          }
          return row[field] ?? ''; // Preserve 0/false values
        });
        aoaData.push(newRow);
      });

      // Create worksheet using aoa_to_sheet (Array of Arrays)
      let worksheet: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(aoaData);

      workBook.Sheets[key] = worksheet;
      workBook.SheetNames.push(key);
    });

    XLSX.writeFile(
      workBook,
      XlsxService.toExportFileName(this.formatExportName(excelFileName), format),
    );
  }

  public async exportExcelWithImagesUsingExcelJS(
    jsons: any,
    fileName: string,
    format: 'xlsx' | 'csv',
  ): Promise<void> {
    if (format === 'csv') {
      const csvWorkbook = new ExcelJS.Workbook();
      const json = jsons[Object.keys(jsons)[0]]; // Export only the first sheet in CSV
      if (!json || json.length === 0) return;

      const worksheet = csvWorkbook.addWorksheet('data');
      const headers = Object.keys(json[0]);
      worksheet.addRow(headers.map((h) => this.formatHeaderForExport(h)));

      for (const row of json) {
        const rowValues: any[] = headers.map((field) => {
          if (['description', 'recommended_corrective_action', 'الوصف'].includes(field)) {
            return this.convertHtmlToPlainText(row[field] ?? '');
          } else if (['Image', 'image', 'صورة'].includes(field) && row[field]) {
            return row[field]; // put image URL as fallback in CSV
          } else {
            return row[field] ?? '';
          }
        });
        worksheet.addRow(rowValues);
      }

      const buffer = await csvWorkbook.csv.writeBuffer();
      FileSaver.saveAs(new Blob([buffer]), `${this.formatExportName(fileName)}.csv`);
      this.toastrService.success(
        this.translateService.translate('Download completed for your observation excel', {
          type: 'CSV',
        }),
      );
      return;
    }

    // XLSX export with images
    const workbook = new ExcelJS.Workbook();

    for (const key of Object.keys(jsons)) {
      const worksheet = workbook.addWorksheet(this.formatExportName(key));
      const json = jsons[key];
      if (!json || json.length === 0) continue;

      const headers = Object.keys(json[0]);
      worksheet.addRow(headers.map((h) => this.formatHeaderForExport(h)));

      for (const row of json) {
        const rowValues: any[] = [];
        let ImageField: string = '';

        for (const field of headers) {
          if (['description', 'recommended_corrective_action', 'الوصف'].includes(field)) {
            rowValues.push(this.convertHtmlToPlainText(row[field] ?? ''));
          } else if (['Image', 'image', 'صورة'].includes(field) && typeof row[field] === 'string') {
            rowValues.push(null); // reserve space for image
            ImageField = field;
          } else {
            rowValues.push(row[field] ?? '');
          }
        }

        const addedRow = worksheet.addRow(rowValues);

        const imgIndex = headers.findIndex((h) => h === ImageField);
        const imageUrl = row[ImageField];
        if (
          imgIndex !== -1 &&
          typeof imageUrl === 'string' &&
          imageUrl.trim().length > 0 &&
          imageUrl.trim() !== '[object Object]'
        ) {
          try {
            const base64 = await this.compressAndResizeImage(imageUrl);
            const imageId = workbook.addImage({
              base64,
              extension: 'jpeg',
            });

            worksheet.addImage(imageId, {
              tl: { col: imgIndex, row: addedRow.number - 1 },
              ext: { width: 100, height: 80 },
              editAs: 'oneCell',
            });
            worksheet.getRow(addedRow.number).height = 60; // Adjust row height (in points, ~0.75px per unit)
          } catch (err) {
            this.logImageError(imageUrl, err);
          }
        }
      }

      worksheet.columns.forEach((col) => {
        col.width = 20;
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    FileSaver.saveAs(new Blob([buffer]), `${this.formatExportName(fileName)}.xlsx`);
    this.toastrService.success(
      this.translateService.translate('Download completed for your observation excel', {
        type: 'excel',
      }),
    );
  }

  createIncrementalWorksheetContext(
    headers: string[],
    sheetName: string,
    format: IncrementalExportFormat,
  ): IncrementalWorksheetContext {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(this.formatExportName(sheetName));
    worksheet.addRow(headers.map((h) => this.formatHeaderForExport(h)));

    const imageColumnIndex = headers.findIndex((h) => ['Image', 'image', 'صورة'].includes(h));

    return {
      workbook,
      worksheet,
      headers,
      imageColumnIndex,
      format,
    };
  }

  async appendIncrementalRows(
    context: IncrementalWorksheetContext,
    rows: any[],
    includeImages: boolean,
  ): Promise<void> {
    // PC-2033: Add all row values synchronously first, then embed images with bounded
    // concurrency. Previously each image was downloaded/compressed serially (await per row),
    // so a page of large (4-5 MB) production images blocked the export loop for a long time
    // before the next `export_excel` page could be fetched, making progress appear frozen.
    const pendingImages: { rowNumber: number; imageUrl: string }[] = [];

    for (const row of rows) {
      const rowValues: any[] = [];

      for (const field of context.headers) {
        if (['description', 'recommended_corrective_action', 'الوصف'].includes(field)) {
          rowValues.push(this.convertHtmlToPlainText(row[field] || ''));
        } else if (
          context.imageColumnIndex !== -1 &&
          context.headers[context.imageColumnIndex] === field
        ) {
          rowValues.push(includeImages ? null : row[field] ?? '');
        } else {
          rowValues.push(row[field] ?? '');
        }
      }

      const addedRow = context.worksheet.addRow(rowValues);

      if (includeImages && context.imageColumnIndex !== -1) {
        const imageUrl = row[context.headers[context.imageColumnIndex]];
        if (
          typeof imageUrl === 'string' &&
          imageUrl.trim().length > 0 &&
          imageUrl.trim() !== '[object Object]'
        ) {
          context.worksheet.getRow(addedRow.number).height = 60;
          pendingImages.push({ rowNumber: addedRow.number, imageUrl });
        } else if (imageUrl && this.imageErrorLogCount < this.maxImageErrorLogs) {
          this.logImageError(imageUrl, new Error('Invalid image URL type'));
        }
      }
    }

    if (pendingImages.length) {
      await this.embedImagesWithConcurrency(context, pendingImages);
    }
  }

  /**
   * Downloads, compresses and embeds the given images into the worksheet using a bounded
   * number of in-flight requests. Image download/compression dominates export time, so
   * parallelizing it (instead of one await per row) keeps the per-page work short and lets
   * the export polling loop advance and report progress smoothly. See PC-2033.
   */
  private async embedImagesWithConcurrency(
    context: IncrementalWorksheetContext,
    images: { rowNumber: number; imageUrl: string }[],
    concurrency = 6,
  ): Promise<void> {
    let cursor = 0;

    const worker = async (): Promise<void> => {
      while (cursor < images.length) {
        const { rowNumber, imageUrl } = images[cursor++];
        try {
          const base64 = await this.compressAndResizeImage(imageUrl);
          const imageId = context.workbook.addImage({
            base64,
            extension: 'jpeg',
          });

          context.worksheet.addImage(imageId, {
            tl: { col: context.imageColumnIndex, row: rowNumber - 1 },
            ext: { width: 100, height: 80 },
            editAs: 'oneCell',
          });
        } catch (err) {
          this.logImageError(imageUrl, err);
        }
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, images.length) }, () => worker());
    await Promise.all(workers);
  }

  resetImageErrorLogCount(): void {
    this.imageErrorLogCount = 0;
  }

  async finalizeIncrementalWorkbook(
    context: IncrementalWorksheetContext,
    fileName: string,
    extraSheets: Record<string, any[]> = {},
  ): Promise<void> {
    if (context.format === 'xlsx') {
      Object.keys(extraSheets).forEach((key) => {
        const rows = extraSheets[key];
        if (!rows || rows.length === 0) return;

        const worksheet = context.workbook.addWorksheet(this.formatExportName(key));
        const headers = Object.keys(rows[0]);
        worksheet.addRow(headers.map((h) => this.formatHeaderForExport(h)));
        rows.forEach((row) => {
          worksheet.addRow(headers.map((field) => row[field] ?? ''));
        });
        worksheet.columns.forEach((col) => {
          col.width = 20;
        });
      });

      context.worksheet.columns.forEach((col) => {
        col.width = 20;
      });

      const buffer = await context.workbook.xlsx.writeBuffer();
      FileSaver.saveAs(new Blob([buffer]), `${this.formatExportName(fileName)}.xlsx`);
      this.toastrService.success(
        this.translateService.translate('Download completed for your observation excel', {
          type: 'excel',
        }),
      );
      return;
    }

    const buffer = await context.workbook.csv.writeBuffer();
    FileSaver.saveAs(new Blob([buffer]), `${this.formatExportName(fileName)}.csv`);
    this.toastrService.success(
      this.translateService.translate('Download completed for your observation excel', {
        type: 'CSV',
      }),
    );
  }

  private async compressAndResizeImage(
    imageUrl: string,
    maxWidth = 600,
    maxHeight = 600,
  ): Promise<string> {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;

    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Image load error'));
    });

    const canvas = document.createElement('canvas');

    // Calculate the resized dimensions
    let { width, height } = img;
    const aspectRatio = width / height;

    if (width > maxWidth || height > maxHeight) {
      if (aspectRatio > 1) {
        width = maxWidth;
        height = maxWidth / aspectRatio;
      } else {
        height = maxHeight;
        width = maxHeight * aspectRatio;
      }
    }

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx?.drawImage(img, 0, 0, width, height);

    // Compress using JPEG (much smaller than PNG)
    return canvas.toDataURL('image/jpeg', 0.8); // 0.8 = compression quality
  }

  private convertHtmlToPlainText(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html;

    let plainText = '';

    div.childNodes.forEach((node: any) => {
      if (node.nodeType === Node.TEXT_NODE) {
        plainText += node.textContent;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        let textContent = node.textContent || '';

        if (node.tagName === 'B' || node.tagName === 'STRONG') {
          textContent = `*${textContent}*`; // Bold text with asterisks
        } else if (node.tagName === 'I' || node.tagName === 'EM') {
          textContent = `_${textContent}_`; // Italic text with underscores
        } else if (node.tagName === 'U') {
          textContent = `~${textContent}~`; // Underline with tilde (~)
        } else if (node.tagName === 'BR') {
          textContent = `\n`; // New line
        } else if (node.tagName === 'UL') {
          textContent = '\n';
          node.childNodes.forEach((liNode) => {
            if (liNode.nodeName === 'LI') {
              textContent += `- ${liNode.textContent}\n`; // Bullet points
            }
          });
        }

        plainText += textContent + ' ';
      }
    });

    return plainText.trim();
  }

  /**
   * Format header for export: use as-is if it contains non-Latin script (e.g. Arabic, Russian),
   * otherwise apply titleCase so English keys stay readable.
   */
  private formatHeaderForExport(str: string): string {
    if (!str || /[\u0600-\u06FF\u0400-\u04FF\u4e00-\u9fff]/.test(str)) {
      return this.formatExportName(str);
    }
    return this.formatExportName(this.titleCase(str));
  }

  private formatExportName(value: string): string {
    return this.nwcTerminologyService.formatTextForCurrentUser(value);
  }

  private titleCase(str: string): string {
    const sentence = str.toLowerCase().split('_');
    for (let i = 0; i < sentence.length; i++) {
      sentence[i] = sentence[i][0].toUpperCase() + sentence[i].slice(1);
    }

    return sentence.join(' ');
  }

  private logImageError(imageUrl: unknown, err: unknown): void {
    if (this.imageErrorLogCount >= this.maxImageErrorLogs) {
      return;
    }

    console.warn('Skipping invalid export image', { imageUrl, err });
    this.imageErrorLogCount += 1;

    if (this.imageErrorLogCount === this.maxImageErrorLogs) {
      console.warn('Further export image errors will be suppressed');
    }
  }
}
